import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import SubtitleOverlayPlayer, {
    SubtitleOverlayPlayerRef,
    SubtitleOverlayPlayerProps,
} from '../../subtitleOverlayPlayer/SubtitleOverlayPlayer';
import {
    SUBTITLE_PREVIEW_BOX_WIDTH_FRAC,
    normalizeSubtitleLineNumber,
} from '../../../utils/autoSubtitleGeometry';
import { previewStyleFromSubtitleBurnRequest } from '../../../utils/subtitleBurnRequest';
import { usePlaybackStore } from '../videoTimeBox/playbackStore';
import { updateSubtitleLineNumberApi } from '../../../service/task';
import { useAlert } from '../../../hooks/useAlert';

type Props = Omit<SubtitleOverlayPlayerProps, 'externalIsPlaying' | 'externalCurrentTime'>;

/**
 * 仅此处订阅播放时间/状态，避免整页 VideoEdit 随 rvfc 高频重渲染。
 * 同时管理字幕位置/缩放/选中状态，支持拖拽和缩放。
 * 进入编辑页时从 /video/task/get 的 subtitleBurnRequest 恢复压制样式（展示层坐标）。
 */
export const VideoEditSubtitleOverlayPlayer = forwardRef<SubtitleOverlayPlayerRef, Props>((props, ref) => {
    const externalCurrentTime = usePlaybackStore((s) => s.currentTime);
    const externalIsPlaying = usePlaybackStore((s) => s.isPlaying);
    const { showError } = useAlert();

    const [subtitlePosition, setSubtitlePosition] = useState<{ x: number; y: number }>({ x: 0.5, y: 0.85 });
    const [subtitleScale, setSubtitleScale] = useState<number>(1.0);
    const [subtitleWidthRatio, setSubtitleWidthRatio] = useState<number>(SUBTITLE_PREVIEW_BOX_WIDTH_FRAC * 1.0);
    const [subtitleSelected, setSubtitleSelected] = useState<boolean>(false);
    const [subtitleLineNumber, setSubtitleLineNumber] = useState(() =>
        normalizeSubtitleLineNumber(props.videoData?.subtitleLineNumber)
    );

    /** 每个 taskId 仅应用一次服务端下发的压制样式，避免覆盖用户拖拽 */
    const burnStyleAppliedTaskIdRef = useRef<number | null>(null);

    useEffect(() => {
        setSubtitleLineNumber(normalizeSubtitleLineNumber(props.videoData?.subtitleLineNumber));
    }, [props.videoData?.id, props.videoData?.subtitleLineNumber]);

    useEffect(() => {
        const taskId = props.videoData?.id;
        const raw = props.videoData?.subtitleBurnRequest;
        if (!taskId || !raw) return;
        if (burnStyleAppliedTaskIdRef.current === taskId) return;

        const style = previewStyleFromSubtitleBurnRequest(raw);
        if (!style) return;

        setSubtitlePosition(style.relativeCenter);
        setSubtitleScale(style.fontScale);
        setSubtitleWidthRatio(style.boxWidthRatio);
        burnStyleAppliedTaskIdRef.current = taskId;
    }, [props.videoData?.id, props.videoData?.subtitleBurnRequest]);

    useEffect(() => {
        burnStyleAppliedTaskIdRef.current = null;
    }, [props.taskId]);

    const handleSubtitleLineNumberChange = useCallback(
        async (nextLineNumber: number): Promise<boolean> => {
            const taskId = props.videoData?.id;
            if (!taskId) {
                showError('任务信息缺失，无法修改行数');
                return false;
            }
            try {
                const res = await updateSubtitleLineNumberApi({
                    id: taskId,
                    subtitleLineNumber: nextLineNumber,
                });
                if (res.code !== 0) {
                    showError(res.msg || '修改字幕行数失败');
                    return false;
                }
                setSubtitleLineNumber(nextLineNumber);
                return true;
            } catch (error: unknown) {
                console.error('修改字幕行数失败:', error);
                showError('修改字幕行数失败，请重试');
                return false;
            }
        },
        [props.videoData?.id, showError]
    );

    const isInteractive = props.interactive ?? false;

    return (
        <SubtitleOverlayPlayer
            ref={ref}
            {...props}
            externalCurrentTime={externalCurrentTime}
            externalIsPlaying={externalIsPlaying}
            interactive={isInteractive}
            subtitlePosition={subtitlePosition}
            subtitleScale={subtitleScale}
            subtitleWidthRatio={subtitleWidthRatio}
            subtitleLineNumber={subtitleLineNumber}
            onSubtitlePositionChange={setSubtitlePosition}
            onSubtitleScaleChange={setSubtitleScale}
            onSubtitleWidthRatioChange={setSubtitleWidthRatio}
            onSubtitlePostTextChange={props.onSubtitlePostTextChange}
            onSubtitleLineNumberChange={handleSubtitleLineNumberChange}
            subtitleSelected={subtitleSelected}
            onSubtitleSelectedChange={setSubtitleSelected}
        />
    );
});

VideoEditSubtitleOverlayPlayer.displayName = 'VideoEditSubtitleOverlayPlayer';
